import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, DataTable, ErrorBox, useAct } from "@/ui/kit";
import { GenerateDocModal } from "@/ui/generator";
import { SharePointFolder, SyncBadge } from "@/ui/sharepoint";
import { SignatureModal } from "@/ui/signature";
import { useFeatures } from "@/lib/features";

export interface DocRow {
  id: string; name: string; mimeType: string; size: number; sha256: string; version: number;
  previousVersionId: string | null; createdAt: string; createdBy: string; templateId?: string | null;
  syncStatus?: string; spWebUrl?: string | null; syncError?: string | null;
  portalVisible?: boolean; portalAccessId?: string | null; portalReviewedAt?: string | null;
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Documentos de um contexto (pessoa, caso, processo ou atividade). */
export function DocumentsPanel({ ctx, title = "Documentos" }: {
  ctx: { personId?: string; caseId?: string; processId?: string; activityId?: string; titleId?: string }; title?: string;
}): JSX.Element {
  const { userName, can } = useLookups();
  const key = ["documentos", ctx];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api.get<DocRow[]>(`/documentos${qs(ctx)}`) });
  const input = useRef<HTMLInputElement>(null);
  const [versionOf, setVersionOf] = useState<string | null>(null);
  const [gen, setGen] = useState(false);
  const [sign, setSign] = useState<DocRow | null>(null);
  const [drag, setDrag] = useState(false);
  const feats = useFeatures();
  const portalCtx = !ctx.activityId && !ctx.titleId && (!!ctx.processId || !!ctx.caseId);
  const portal = useAct((x: { id: string; visible?: boolean; reviewed?: boolean }) => api.patch(`/documentos/${x.id}/portal`, { visible: x.visible, reviewed: x.reviewed }), [["documentos"]]);
  const canGenerate = !ctx.activityId && !ctx.titleId && (ctx.personId || ctx.caseId || ctx.processId);
  const upload = useAct(async (file: File) => {
    const f = new FormData();
    for (const [k, v] of Object.entries(ctx)) if (v) f.append(k, v);
    if (versionOf) f.append("previousVersionId", versionOf);
    f.append("file", file); // o arquivo por último: campos antes do stream
    return api.upload(`/documentos`, f);
  }, [["documentos"]], () => setVersionOf(null));

  const spCtx = !ctx.activityId && !ctx.titleId && (ctx.personId || ctx.caseId || ctx.processId) ? { personId: ctx.personId, caseId: ctx.caseId, processId: ctx.processId } : null;
  return (
    <div className="stack gap-16">
    <div className={`dropzone ${drag ? "over" : ""}`}
      onDragOver={(e) => { if (can("documento.enviar") && e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f && can("documento.enviar")) { setVersionOf(null); upload.mutate(f); } }}>
    <Card title={title} actions={can("documento.enviar") && (
      <>
        {canGenerate && <button className="btn small primary" onClick={() => setGen(true)}>Gerar documento</button>}
        <input ref={input} type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} />
        <button className="btn small" disabled={upload.isPending} onClick={() => { setVersionOf(null); input.current?.click(); }}>
          {upload.isPending ? "Enviando…" : "Enviar arquivo"}
        </button>
      </>
    )}>
      {sign && <SignatureModal doc={sign} onClose={() => setSign(null)} />}
      {gen && <GenerateDocModal ctx={{ personId: ctx.personId, caseId: ctx.caseId, processId: ctx.processId }} onClose={() => setGen(false)} />}
      <ErrorBox error={error ?? upload.error} />
      <DataTable
        rows={data}
        rowKey={(d) => d.id}
        empty="Nenhum documento."
        columns={[
          { key: "n", label: "Arquivo", render: (d) => (
            <><button className="link-btn inline" onClick={() => void api.download(`/documentos/${d.id}/download?inline=1`, d.name, true)}>{d.name}</button>
            {d.templateId && <> <Badge tone="muted">gerado</Badge></>}
            {d.portalVisible && <> <Badge tone="info">no portal</Badge></>}
            {d.portalAccessId && <> <Badge tone={d.portalReviewedAt ? "muted" : "warn"}>{d.portalReviewedAt ? "enviado pelo cliente" : "enviado pelo cliente — conferir"}</Badge></>}
            {d.syncStatus && <> <SyncBadge status={d.syncStatus} webUrl={d.spWebUrl} error={d.syncError} /></>}</>
          ) },
          { key: "v", label: "Versão", width: 70, render: (d) => (d.version > 1 ? <Badge tone="info">v{d.version}</Badge> : "v1") },
          { key: "s", label: "Tamanho", width: 90, render: (d) => fmtSize(d.size) },
          { key: "u", label: "Enviado por", render: (d) => <>{userName(d.createdBy)}<div className="muted small">{fmtDateTime(d.createdAt)}</div></> },
          { key: "a", label: "", width: portalCtx ? 380 : 260, align: "right", render: (d) => (
            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button className="btn small" onClick={() => void api.download(`/documentos/${d.id}/download`, d.name)}>Baixar</button>
              {feats.on("assinatura") && d.mimeType === "application/pdf" && can("documento.enviar") && <button className="btn small" onClick={() => setSign(d)}>Assinar</button>}
              {can("documento.enviar") && <button className="btn small" onClick={() => { setVersionOf(d.id); input.current?.click(); }}>Nova versão</button>}
              {portalCtx && can("documento.enviar") && !d.portalAccessId && <button className="btn small ghost" title="Aparece no portal do cliente (se o portal estiver liberado para este cliente)" disabled={portal.isPending}
                onClick={() => portal.mutate({ id: d.id, visible: !d.portalVisible })}>{d.portalVisible ? "Tirar do portal" : "Mostrar no portal"}</button>}
              {d.portalAccessId && !d.portalReviewedAt && can("documento.enviar") && <button className="btn small" onClick={() => portal.mutate({ id: d.id, reviewed: true })}>Conferido</button>}
            </div>
          ) },
        ]}
      />
      <div className="muted small pad">Arraste arquivos para cá ou use "Enviar arquivo". Tudo é copiado automaticamente para a pasta do SharePoint e para o espelho de segurança.</div>
    </Card>
    </div>
    {spCtx && <SharePointFolder ctx={spCtx} />}
    </div>
  );
}
